import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Linkedin,
  Facebook,
  Instagram,
  Twitter,
  FileText,
  Calendar,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/Card';
import { Badge, getStatusVariant } from '../../components/Badge';
import { Button } from '../../components/Button';
import { socialPostsApi, contentApi } from '../../lib/api';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  isToday,
} from 'date-fns';
import type { SocialPost, Content, SocialPlatform } from '../../types';

function getPlatformIcon(platform: SocialPlatform) {
  switch (platform) {
    case 'linkedin':
      return <Linkedin className="w-3 h-3" />;
    case 'facebook':
      return <Facebook className="w-3 h-3" />;
    case 'instagram':
      return <Instagram className="w-3 h-3" />;
    case 'twitter':
      return <Twitter className="w-3 h-3" />;
    default:
      return null;
  }
}

function getPlatformColor(platform: SocialPlatform) {
  switch (platform) {
    case 'linkedin':
      return 'bg-blue-500';
    case 'facebook':
      return 'bg-indigo-500';
    case 'instagram':
      return 'bg-pink-500';
    case 'twitter':
      return 'bg-sky-500';
    default:
      return 'bg-gray-500';
  }
}

interface CalendarEvent {
  id: string;
  type: 'social' | 'content';
  title: string;
  date: Date;
  platform?: SocialPlatform;
  status: string;
}

export function ContentCalendar() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const { data: socialPosts } = useQuery({
    queryKey: ['social-posts'],
    queryFn: () => socialPostsApi.list({ limit: 100 }),
  });

  const { data: content } = useQuery({
    queryKey: ['content'],
    queryFn: () => contentApi.list({ limit: 100 }),
  });

  const posts = (socialPosts?.posts || []) as SocialPost[];
  const contentList = (content?.content || []) as Content[];

  // Combine posts and content into calendar events
  const events = useMemo(() => {
    const allEvents: CalendarEvent[] = [];

    // Add social posts
    posts.forEach((post) => {
      const date = post.scheduled_for || post.published_at;
      if (date) {
        allEvents.push({
          id: post.id,
          type: 'social',
          title: post.post_text.substring(0, 50) + (post.post_text.length > 50 ? '...' : ''),
          date: new Date(date),
          platform: post.platform,
          status: post.status,
        });
      }
    });

    // Add content
    contentList.forEach((item) => {
      const date = item.scheduled_for || item.published_at;
      if (date) {
        allEvents.push({
          id: item.id,
          type: 'content',
          title: item.title,
          date: new Date(date),
          status: item.status,
        });
      }
    });

    return allEvents;
  }, [posts, contentList]);

  // Get calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Get events for a specific day
  const getEventsForDay = (day: Date) => {
    return events.filter((event) => isSameDay(event.date, day));
  };

  // Get events for selected date
  const selectedDateEvents = selectedDate ? getEventsForDay(selectedDate) : [];

  const navigatePrevious = () => setCurrentMonth(subMonths(currentMonth, 1));
  const navigateNext = () => setCurrentMonth(addMonths(currentMonth, 1));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Content Calendar</h1>
          <p className="text-gray-500 mt-1">Plan and schedule your content</p>
        </div>
        <Button>
          <Calendar className="w-4 h-4" />
          Schedule Post
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex items-center justify-between border-b">
            <div className="flex items-center gap-4">
              <button
                onClick={navigatePrevious}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-lg font-semibold">
                {format(currentMonth, 'MMMM yyyy')}
              </h2>
              <button onClick={navigateNext} className="p-2 hover:bg-gray-100 rounded-lg">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <button
              onClick={() => setCurrentMonth(new Date())}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Today
            </button>
          </CardHeader>
          <CardContent className="p-0">
            {/* Day headers */}
            <div className="grid grid-cols-7 border-b">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div
                  key={day}
                  className="py-3 text-center text-sm font-medium text-gray-500"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7">
              {calendarDays.map((day) => {
                const dayEvents = getEventsForDay(day);
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                const isTodayDate = isToday(day);

                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => setSelectedDate(day)}
                    className={`
                      min-h-24 p-2 border-b border-r text-left transition-colors
                      ${isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                      ${isSelected ? 'bg-blue-50 ring-2 ring-blue-500 ring-inset' : ''}
                      hover:bg-gray-50
                    `}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={`
                          text-sm font-medium
                          ${!isCurrentMonth ? 'text-gray-400' : ''}
                          ${isTodayDate ? 'w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center' : ''}
                        `}
                      >
                        {format(day, 'd')}
                      </span>
                      {dayEvents.length > 0 && (
                        <span className="text-xs text-gray-400">{dayEvents.length}</span>
                      )}
                    </div>

                    {/* Event indicators */}
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map((event) => (
                        <div
                          key={event.id}
                          className={`
                            flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-white
                            ${event.type === 'social' ? getPlatformColor(event.platform!) : 'bg-purple-500'}
                          `}
                        >
                          {event.type === 'social' ? (
                            getPlatformIcon(event.platform!)
                          ) : (
                            <FileText className="w-3 h-3" />
                          )}
                          <span className="truncate">{event.title.substring(0, 15)}</span>
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-gray-500 pl-1">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Selected Day Details */}
        <Card>
          <CardHeader>
            <CardTitle>
              {selectedDate
                ? format(selectedDate, 'EEEE, MMMM d')
                : 'Select a date'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {selectedDate ? (
              selectedDateEvents.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {selectedDateEvents.map((event) => (
                    <div key={event.id} className="p-4">
                      <div className="flex items-start gap-3">
                        <div
                          className={`
                            p-2 rounded-lg text-white
                            ${event.type === 'social' ? getPlatformColor(event.platform!) : 'bg-purple-500'}
                          `}
                        >
                          {event.type === 'social' ? (
                            getPlatformIcon(event.platform!)
                          ) : (
                            <FileText className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 line-clamp-2">
                            {event.title}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant={getStatusVariant(event.status)}>
                              {event.status}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {format(event.date, 'h:mm a')}
                            </span>
                          </div>
                          {event.platform && (
                            <Badge variant="default" className="mt-2">
                              {event.platform}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No events scheduled</p>
                </div>
              )
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Click a date to see details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Events */}
      <Card>
        <CardHeader>
          <CardTitle>Upcoming This Week</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-100">
            {events
              .filter((e) => e.date >= new Date())
              .sort((a, b) => a.date.getTime() - b.date.getTime())
              .slice(0, 10)
              .map((event) => (
                <div key={event.id} className="p-4 flex items-center gap-4">
                  <div
                    className={`
                      p-2 rounded-lg text-white
                      ${event.type === 'social' ? getPlatformColor(event.platform!) : 'bg-purple-500'}
                    `}
                  >
                    {event.type === 'social' ? (
                      getPlatformIcon(event.platform!)
                    ) : (
                      <FileText className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{event.title}</p>
                    <p className="text-sm text-gray-500">
                      {format(event.date, 'EEE, MMM d • h:mm a')}
                    </p>
                  </div>
                  <Badge variant={getStatusVariant(event.status)}>{event.status}</Badge>
                </div>
              ))}
            {events.filter((e) => e.date >= new Date()).length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No upcoming events</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
